import $ from "jquery";
import { GraphForm } from "./graph_form";
import { ConditionPanel } from "./condition_panel";
import { TargetDate } from "./target_date";
import { Scattergram, ScattergramPoint } from "./scattergram";

// Interface definitions
interface ScattergramOptions {
  width?: number;
  height?: number;
  [key: string]: any;
}

interface Location {
  x: number;
  y: number;
}

interface HttpErrorResponse {
  status: number;
  statusText?: string;
  [key: string]: any;
}

interface InvalidParameterCombination {
  type?: string;
  partition?: string;
  hasDimension?: boolean;
}

declare global {
  interface Window {
    tablemill: any;
  }
}

class DashboardsView extends GraphForm {
  private static readonly PARTITION_NAMES: { [key: string]: string } = {
    yearly: "年",
    monthly: "月",
    weekly: "週",
    daily: "日",
  };

  protected formElement: JQuery;
  private targetDate: TargetDate;
  private conditionPanel: ConditionPanel;
  private graph!: Scattergram; // 初期化で設定されるため!を使用

  constructor(formElement: JQuery) {
    super();
    this.formElement = formElement;
    this.targetDate = new TargetDate($("#target"));
    this.conditionPanel = new ConditionPanel(this.formElement);
  }

  initialize(
    canvas: JQuery,
    magnification: number,
    location: Location,
    options: ScattergramOptions,
    invalidParameterCombinations: InvalidParameterCombination[],
  ): void {
    this.targetDate.initialize();
    this.graph = new Scattergram(canvas, magnification, location, options);
    this.conditionPanel.initialize(invalidParameterCombinations);
  }

  anyDimensionHasSelected(): boolean {
    return this.conditionPanel.anyDimensionHasSelected();
  }

  onZoomInButtonClick(callback: (event: JQuery.ClickEvent) => void): void {
    $(".zoom_in").click((e: JQuery.ClickEvent) => {
      callback(e);
    });
  }

  onZoomOutButtonClick(callback: (event: JQuery.ClickEvent) => void): void {
    $(".zoom_out").click((e: JQuery.ClickEvent) => {
      callback(e);
    });
  }

  showBadPartitionMessage(partition: string): void {
    const partitionName =
      DashboardsView.PARTITION_NAMES[partition] || partition;
    this.graph.drawMessage(`
      集計単位が「${partitionName}」のとき、このグラフは表示できません。
    `);
  }

  showKeywordNotFoundMessage(keyword: string): void {
    this.graph.drawMessage(`
      「${keyword}」は見付かりませんでした。
      別の単語をお試しください。
    `);
  }

  showNoDataMessage(keyword?: string): void {
    if (keyword) {
      this.showKeywordNotFoundMessage(keyword);
    } else {
      this.graph.drawMessage(`
        この時期のトレンドデータは見付かりませんでした。
        他の期間をお試しください。
      `);
    }
  }

  showHttpErrorMessage(response: HttpErrorResponse): void {
    const currentDate = new Date().toLocaleString("ja-JP");
    this.graph.drawErrorMessage(`
      申し訳ありません、通信エラーが発生しました。
      少し時間をおいて再度お試しください。
      ステータス: ${response.status}, ${currentDate}
    `);
  }

  render(points: ScattergramPoint[]): void {
    this.graph.drawPoints(points);
    this.enableCsvButton($(".scattergram_box .csv_button"));
    this.enableCsvButton($(".keyword_ranking_box .csv_button"));
  }

  // Getters for accessing sub-components
  getTargetDate(): TargetDate {
    return this.targetDate;
  }

  getConditionPanel(): ConditionPanel {
    return this.conditionPanel;
  }

  getGraph(): Scattergram {
    return this.graph;
  }

  // Type-safe partition name getter
  getPartitionName(partition: string): string {
    return DashboardsView.PARTITION_NAMES[partition] || partition;
  }
}

// Global export
window.tablemill = window.tablemill || {};
window.tablemill.DashboardsView = DashboardsView;

export { DashboardsView };
